import React, { useEffect, useId, useState } from 'react';
import mermaid from 'mermaid';

let initialized = false;

function ensureMermaidInit() {
  if (initialized) return;
  mermaid.initialize({
    startOnLoad: false,
    securityLevel: 'strict',
    theme: 'base',
    fontFamily: '"IBM Plex Sans", "Inter", system-ui, -apple-system, sans-serif',
    themeVariables: {
      background: '#ffffff',
      primaryColor: '#f8fafc',
      primaryTextColor: '#0f172a',
      primaryBorderColor: '#334155',
      secondaryColor: '#eff6ff',
      secondaryTextColor: '#0f172a',
      secondaryBorderColor: '#1d4ed8',
      tertiaryColor: '#f0fdf4',
      tertiaryTextColor: '#0f172a',
      tertiaryBorderColor: '#15803d',
      lineColor: '#334155',
      textColor: '#0f172a',
      mainBkg: '#f8fafc',
      nodeBorder: '#334155',
      clusterBkg: '#f8fafc',
      clusterBorder: '#64748b',
      titleColor: '#0f172a',
      edgeLabelBackground: '#ffffff',
      fontSize: '12px',
    },
    flowchart: {
      // Native SVG text labels: Chromium's print pipeline drops foreignObject HTML labels,
      // which printed the paper figure as unlabeled boxes (D-HP-ADMIN-069 §2).
      htmlLabels: false,
      curve: 'linear',
      nodeSpacing: 16,
      rankSpacing: 28,
      padding: 8,
      subGraphTitleMargin: { top: 6, bottom: 6 },
      useMaxWidth: true,
    },
  });
  initialized = true;
}

export const MermaidDiagram = ({ chart, code, title, subtitle, figureLabel }) => {
  const source = (chart || code || '').trim();
  const rawId = useId();
  const safeId = `mmd-${rawId.replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const [svg, setSvg] = useState('');
  const [renderError, setRenderError] = useState('');

  useEffect(() => {
    let cancelled = false;
    if (!source) {
      setSvg('');
      setRenderError('');
      return undefined;
    }
    ensureMermaidInit();
    (async () => {
      try {
        const uniqueRenderId = `${safeId}-${Date.now().toString(36)}`;
        const { svg: rendered } = await mermaid.render(uniqueRenderId, source);
        if (!cancelled) {
          const responsiveSvg = rendered.replace(
            /<svg([^>]*)>/i,
            (match, attrs) => {
              const cleaned = attrs.replace(/\s+height="[^"]*"/i, '');
              return `<svg${cleaned} style="width: 100%; height: auto; display: block; margin: 0 auto;">`;
            }
          );
          setSvg(responsiveSvg);
          setRenderError('');
        }
      } catch (err) {
        if (!cancelled) {
          setRenderError(err?.message || 'Mermaid rendering error');
          setSvg('');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [source, safeId]);

  return (
    <figure className="homepage-muted-panel p-3 space-y-2" style={{ border: '1px solid #cbd5e1', background: '#ffffff', color: '#0f172a' }}>
      {(title || subtitle) ? (
        <div className="border-b pb-2" style={{ borderColor: '#e2e8f0' }}>
          {title ? (
            <figcaption className="font-bold text-xs" style={{ color: '#0f172a' }}>
              {figureLabel ? <span className="font-mono mr-1">{figureLabel}</span> : null}
              {title}
            </figcaption>
          ) : null}
          {subtitle ? <div className="text-xs" style={{ color: '#475569', fontSize: '11px' }}>{subtitle}</div> : null}
        </div>
      ) : null}

      {svg ? (
        <div
          className="mermaid-svg-container"
          style={{
            background: '#ffffff',
            color: '#0f172a',
            padding: '10px 6px',
            overflowX: 'hidden',
          }}
        >
          <div
            style={{ width: '100%' }}
            dangerouslySetInnerHTML={{ __html: svg }}
          />
        </div>
      ) : null}

      {renderError ? (
        <div className="text-xs text-error font-mono">{renderError}</div>
      ) : null}
    </figure>
  );
};

export default MermaidDiagram;
